import { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { useTheme } from 'styled-components';
import useLocalStorage from 'use-local-storage';

import { clearLocalStorageUser, updateLocalStorageUser } from '@/utils/tokens';

import { currentUser, login } from '../../services/auth';
import { IUser } from '../../types';
import { ActionKind } from './actions';
import { authReducer, initialState } from './reducer';
import { ISignIn, State } from './types';

export const LOCAL_STORAGE_KEY = '__USER__';

const AuthContext = createContext(
  {} as {
    signin: (data: ISignIn) => void;
    signout: () => void;
    turnOffInitialDemoMode: () => void;
  } & State,
);

// provider component that wraps your app and makes auth object
export function ProvideAuth({ children }: { children: React.ReactNode }) {
  const auth = useProvideAuth();

  // we only want to render the underlying app after we assert for the presence of a current user.
  return (
    <AuthContext.Provider value={auth}>
      {!auth.loadingInitial && children}
    </AuthContext.Provider>
  );
}

// hook for child components to get the auth object and re-render when it changes
export const useAuth = () => {
  return useContext(AuthContext);
};

// provider hook that creates auth object and handles state
function useProvideAuth() {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [user, _] = useLocalStorage<IUser | null>(LOCAL_STORAGE_KEY, null);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);

  // we use theme hook to get the signined method
  const theme: any = useTheme();
  const signedInMethod = theme.current;

  useEffect(() => {
    const checkUser = async () => {
      if (!user?.access_token) {
        setLoadingInitial(false);
        return;
      }

      try {
        const authenticatedUser = await currentUser();
        if (authenticatedUser.email) {
          dispatch({ type: ActionKind.LoginSuccess, payload: user });
        } else {
          throw new Error('No user');
        }
      } catch (error) {
        dispatch({ type: ActionKind.Logout, payload: {} });
      } finally {
        setLoadingInitial(false);
      }
    };

    checkUser();
  }, []);

  // manipulate state of the auth context only using below functions.
  // so we can easily keep tack of the state in single place.

  // redirection logic is in the Layout components
  const signin = ({ method, email, password }: ISignIn) => {
    dispatch({ type: ActionKind.Login, payload: {} });
    login({ method, email, password })
      .then((res) => {
        // modify API response by adding signed in method

        // we enable demo mode by default for all the users with 'demo' in their email
        const initialDemoMode = email.toLowerCase().includes('demo');

        const payload = { ...(res as IUser), initialDemoMode, signedInMethod };
        updateLocalStorageUser({ ...payload, initialDemoMode });
        dispatch({ type: ActionKind.LoginSuccess, payload });
      })
      .catch(async (e) => {
        const err = await e.response.json();
        dispatch({ type: ActionKind.LoginFailed, payload: err.error });
      });
  };

  const signout = async () => {
    clearLocalStorageUser();
    dispatch({ type: ActionKind.Logout, payload: {} });
  };

  const turnOffInitialDemoMode = () => {
    if (state.user) {
      const payload = { ...state.user, initialDemoMode: false };
      updateLocalStorageUser(payload);
      dispatch({ type: ActionKind.LoginSuccess, payload });
    }
  };

  return { ...state, loadingInitial, turnOffInitialDemoMode, dispatch, signin, signout };
}
